import type { PatientFormData } from '@/pages/main/patient/types/patientTypes';
import { findExactSerialNumberMatch } from '@/pages/main/patient/utils/patientSerialNumberMatch';
import type { PatientAddress } from '@/pages/main/patientAddresses/types';
import type { VisitScheduleEvent } from '../types';

export function shouldShowVisitDeleteButton(visit: VisitScheduleEvent | null | undefined): boolean {
  return !!visit && !visit.is_locked;
}

/**
 * 受診者 set after 検索: the one whose 受診者番号 equals the keyword (HIT-14771, A2/R2 matched
 * 受診者番号 with `=`), otherwise the lowest 受診者番号. `list` must not be empty.
 */
export function pickSearchedPatient(list: PatientFormData[], keyword: string): PatientFormData {
  return (
    findExactSerialNumberMatch(list, keyword) ??
    list.reduce((min, p) =>
      Number(p.serial_number ?? Infinity) < Number(min.serial_number ?? Infinity) ? p : min
    )
  );
}

/** Old system: 受診者 Select only shows patient name (without address). */
export function getPatientNameOnly(patient: PatientFormData): string {
  const name = patient.current_patient_information?.name ?? '';
  return name || `#${patient.id}`;
}

function formatAddressWithName(
  address1?: string | null,
  address2?: string | null,
  address3?: string | null,
  name?: string | null
): string {
  const address = `${address1?.trim() ?? ''}${address2?.trim() ?? ''}`;
  const room = address3?.trim() ?? '';
  const trimmedName = name?.trim() ?? '';

  return [address, room, trimmedName].filter(Boolean).join(' ');
}

/** RA2 display: address1+address2 + half-width space + building/room + half-width space + address_name. */
export function getAddressLabel(
  info:
    | { address_name?: string; address1?: string; address2?: string; address3?: string }
    | null
    | undefined
): string {
  if (!info) return '';
  return formatAddressWithName(info.address1, info.address2, info.address3, info.address_name);
}

/**
 * Pick the patient_informations row active at visitDate.
 *
 * patient_informations has no `available_from` column — period start is implicit
 * (= prev record's available_to + 1). So we filter rows whose `available_to >=
 * visitDate` (the record was still ending after visitDate) and pick the one with
 * the SMALLEST available_to among valid ones — that's the record whose tail is
 * closest to visitDate, i.e. the historically-active record at that date.
 *
 * NULL available_to = open-ended ("current"), sorted last.
 */
export function pickActivePatientInformation(
  list: Array<{ available_to?: string | null } & Record<string, unknown>> | undefined,
  visitDate: string
): (Record<string, unknown> & { available_to?: string | null }) | undefined {
  if (!list || list.length === 0) return undefined;
  const valid = list.filter(patient => {
    if (!patient.available_to || patient.available_to === '0000-00-00') return true;
    return patient.available_to >= visitDate;
  });
  if (valid.length === 0) return undefined;
  valid.sort((a, b) => {
    if (!a.available_to || a.available_to === '0000-00-00') return 1;
    if (!b.available_to || b.available_to === '0000-00-00') return -1;
    return a.available_to.localeCompare(b.available_to);
  });
  return valid[0];
}

/** RA2 display: address1+address2 + half-width space + building/room + half-width space + name. */
export function formatPatientAddressLabel(address: PatientAddress): string {
  return formatAddressWithName(address.address1, address.address2, address.address3, address.name);
}

export function resolveInitialPatientAddressLabel({
  addressList,
  baseAddressLabel,
  preferBaseAddress = false,
}: {
  addressList: PatientAddress[];
  baseAddressLabel?: string;
  preferBaseAddress?: boolean;
}): string {
  const trimmedBaseAddressLabel = baseAddressLabel?.trim() ?? '';

  if (preferBaseAddress && trimmedBaseAddressLabel) {
    return trimmedBaseAddressLabel;
  }

  const defaultAddress = addressList.find(address => address.is_default === true);
  if (defaultAddress) {
    return formatPatientAddressLabel(defaultAddress);
  }

  return trimmedBaseAddressLabel;
}

export function getPatientBaseAddressFields(
  info:
    | { address_name?: unknown; address1?: unknown; address2?: unknown; address3?: unknown }
    | null
    | undefined,
  fallback: {
    patient_base_address1?: string | null;
    patient_base_address2?: string | null;
    patient_base_address3?: string | null;
    patient_base_address_name?: string | null;
  } = {}
): {
  patient_base_address1?: string | null;
  patient_base_address2?: string | null;
  patient_base_address3?: string | null;
  patient_base_address_name?: string | null;
} {
  if (!info) {
    return {
      patient_base_address1: fallback.patient_base_address1 ?? undefined,
      patient_base_address2: fallback.patient_base_address2 ?? undefined,
      patient_base_address3: fallback.patient_base_address3 ?? undefined,
      patient_base_address_name: fallback.patient_base_address_name ?? undefined,
    };
  }

  const toStringOrUndefined = (value: unknown) => (typeof value === 'string' ? value : undefined);

  return {
    patient_base_address1: toStringOrUndefined(info.address1),
    patient_base_address2: toStringOrUndefined(info.address2),
    patient_base_address3: toStringOrUndefined(info.address3),
    patient_base_address_name: toStringOrUndefined(info.address_name),
  };
}

export function normalizeAddressLabelToOptions(
  label: string | null | undefined,
  optionLabels: string[]
): string {
  const trimmedLabel = label?.trim() ?? '';
  if (!trimmedLabel) return '';

  const compactLabel = compactAddressLabel(trimmedLabel);
  const exactMatched = optionLabels.find(option => option === trimmedLabel);
  if (exactMatched) return exactMatched;

  const compactMatches = optionLabels.filter(option => compactAddressLabel(option) === compactLabel);
  const matched = compactMatches.length === 1 ? compactMatches[0] : undefined;

  return matched ?? trimmedLabel;
}

function compactAddressLabel(label: string): string {
  return label.replace(/[\s\u3000]+/gu, '');
}
